import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { treatmentSvcAxiosInstance } from "@/constants";
import type {
    Clinic,
    ClinicListResponse,
    ClinicQueryParams,
    CreateClinicRequest,
    UpdateClinicRequest,
} from "@/types/clinic.types";
import { invalidateListQueries } from "@/utils/query-helpers";

// Query keys for React Query cache management
const queryKeys = {
    clinics: () => ["clinics"] as const,
    clinic: (id: string) => ["clinics", id] as const,
    clinicList: (params: ClinicQueryParams) => ["clinics", "list", params] as const,
    assignedClinics: () => ["clinics", "assigned"] as const,
};

// API endpoints (PRP Service)
const endpoints = {
    // Get all clinics with optional filtering and pagination
    getClinics: async (params: ClinicQueryParams = {}) => {
        const response = await treatmentSvcAxiosInstance.get<ClinicListResponse>("/clinics", {
            params,
        });
        return response.data;
    },

    // Get assigned clinics for the current user (handles manager vs regular user logic on backend)
    getAssignedClinics: async (): Promise<Clinic[]> => {
        const response = await treatmentSvcAxiosInstance.get<Clinic[]>("/clinics/assigned");
        return response.data;
    },

    // Get a single clinic by ID
    getClinic: async (id: string) => {
        const response = await treatmentSvcAxiosInstance.get<Clinic>(`/clinics/${id}`);
        if (!response.data) {
            throw new Error(`Clinic with id ${id} not found`);
        }
        return response.data;
    },

    // Create a new clinic
    // Note: treatment-svc returns Clinic directly, not wrapped in { data: Clinic }
    createClinic: async (data: CreateClinicRequest) => {
        const response = await treatmentSvcAxiosInstance.post<Clinic>("/clinics", data);
        if (!response.data) {
            throw new Error("Failed to create clinic");
        }
        return response.data;
    },

    // Update an existing clinic
    // Note: treatment-svc returns Clinic directly, not wrapped in { data: Clinic }
    updateClinic: async ({ id, data }: { id: string; data: UpdateClinicRequest }) => {
        const response = await treatmentSvcAxiosInstance.put<Clinic>(`/clinics/${id}`, data);
        if (!response.data) {
            throw new Error(`Failed to update clinic with id ${id}`);
        }
        return response.data;
    },

    // Delete a clinic
    deleteClinic: async (id: string) => {
        const response = await treatmentSvcAxiosInstance.delete<{
            message: string;
            success: boolean;
        }>(`/clinics/${id}`);
        return response.data;
    },
};

// React Query hooks

/**
 * Hook to fetch all clinics with optional filtering and pagination
 */
export const useClinics = (params: ClinicQueryParams = {}) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.clinicList(params),
        queryFn: () => endpoints.getClinics(params),
        enabled: !!auth, // Only run when authenticated
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to fetch assigned clinics for the current user
 * Backend handles: returns all clinics if user is manager, otherwise returns only assigned clinics
 */
export const useAssignedClinics = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.assignedClinics(),
        queryFn: () => endpoints.getAssignedClinics(),
        enabled: !!auth, // Only run when authenticated
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to fetch a single clinic by ID
 */
export const useClinic = (id: string) => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.clinic(id),
        queryFn: async () => {
            const clinic = await endpoints.getClinic(id);
            return clinic;
        },
        enabled: !!auth && !!id, // Only run when authenticated and ID is provided
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to create a new clinic
 */
export const useCreateClinic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.createClinic,
        onSuccess: () => {
            invalidateListQueries(queryClient, "clinics");
            queryClient.invalidateQueries({ queryKey: queryKeys.assignedClinics() });
        },
    });
};

/**
 * Hook to update an existing clinic
 */
export const useUpdateClinic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.updateClinic,
        onSuccess: (data, variables) => {
            // Update the specific clinic in cache
            queryClient.setQueryData(queryKeys.clinic(variables.id), data);
            invalidateListQueries(queryClient, "clinics");
            queryClient.invalidateQueries({ queryKey: queryKeys.assignedClinics() });
        },
    });
};

/**
 * Hook to delete a clinic
 */
export const useDeleteClinic = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: endpoints.deleteClinic,
        onSuccess: (_, id) => {
            // Remove the clinic from cache
            queryClient.removeQueries({ queryKey: queryKeys.clinic(id) });
            invalidateListQueries(queryClient, "clinics");
            queryClient.invalidateQueries({ queryKey: queryKeys.assignedClinics() });
        },
    });
};
